import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  useListTodosQuery,
  useClaimTodoMutation,
  useCompleteTodoMutation,
  useReleaseTodoMutation,
} from '../../redux/api/todoApi';
import { useApiErrorToast } from '../../hooks/useApiError';
import TodoCard from './TodoCard';

const FILTERS = [
  { key: 'all', label: 'Alle' },
  { key: 'open', label: 'Offen' },
  { key: 'mine', label: 'Meine' },
  { key: 'done', label: 'Erledigt' },
];

const TodoBoard = () => {
  const currentEvent = useSelector((state) => state.auth.currentEvent);
  const [filter, setFilter] = useState('open');
  const apiErrorToast = useApiErrorToast();

  const queryArgs = useMemo(() => {
    const args = { eventId: currentEvent?.id };
    if (filter === 'open') args.status = 'OPEN,IN_PROGRESS';
    if (filter === 'mine') args.mine = true;
    if (filter === 'done') args.status = 'DONE';
    return args;
  }, [filter, currentEvent?.id]);

  const { data: todos, isLoading, refetch } = useListTodosQuery(queryArgs, {
    skip: !currentEvent?.id,
  });

  const [claim] = useClaimTodoMutation();
  const [complete] = useCompleteTodoMutation();
  const [release] = useReleaseTodoMutation();

  const handleClaim = async (id) => {
    try {
      await claim(id).unwrap();
      toast.success('Aufgabe übernommen');
    } catch (err) {
      apiErrorToast(err, 'Übernehmen fehlgeschlagen');
      refetch();
    }
  };
  const handleComplete = async (id) => {
    try {
      await complete(id).unwrap();
      toast.success('Aufgabe erledigt');
    } catch (err) {
      apiErrorToast(err, 'Erledigen fehlgeschlagen');
    }
  };
  const handleRelease = async (id) => {
    try {
      await release(id).unwrap();
      toast.success('Aufgabe freigegeben');
    } catch (err) {
      apiErrorToast(err, 'Freigeben fehlgeschlagen');
    }
  };

  // Group by category for the desktop board
  const grouped = useMemo(() => {
    const out = new Map();
    (todos || []).forEach((t) => {
      const key = t.category?.id || 'uncat';
      if (!out.has(key)) {
        out.set(key, { category: t.category, items: [] });
      }
      out.get(key).items.push(t);
    });
    return Array.from(out.values());
  }, [todos]);

  if (!currentEvent?.id) {
    return (
      <div className='alert alert-info'>
        Bitte zuerst ein Event in der Top-Navigation auswählen.
      </div>
    );
  }

  return (
    <div>
      <div className='d-flex justify-content-between align-items-center mb-3'>
        <h4 className='mb-0'>Aufgaben — {currentEvent.name}</h4>
        <Link to='/todo/new' className='btn btn-success'>
          <i className='fas fa-plus me-2' />
          Neue Aufgabe
        </Link>
      </div>

      <div className='btn-group mb-3' role='group'>
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type='button'
            className={`btn btn-sm ${filter === f.key ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p>Wird geladen…</p>
      ) : grouped.length === 0 ? (
        <div className='alert alert-light'>Keine Aufgaben.</div>
      ) : (
        grouped.map(({ category, items }) => (
          <div key={category?.id || 'uncat'} className='mb-4'>
            <h5
              className='border-bottom pb-1'
              style={{ borderColor: category?.color || '#ccc' }}
            >
              {category?.icon && (
                <i
                  className={`fas fa-${category.icon} me-2`}
                  style={{ color: category?.color }}
                />
              )}
              {category?.name || 'Ohne Kategorie'}{' '}
              <span className='badge bg-light text-dark'>{items.length}</span>
            </h5>
            {items.map((t) => (
              <TodoCard
                key={t.id}
                todo={t}
                onClaim={handleClaim}
                onComplete={handleComplete}
                onRelease={handleRelease}
              />
            ))}
          </div>
        ))
      )}
    </div>
  );
};

export default TodoBoard;
